import { useEffect, useRef, useState } from "react";

// ---- Request with fallback + in-flight tracking ----
let inFlight = 0;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn(inFlight > 0));
export const isLocating = () => inFlight > 0;

export const useIsLocating = () => {
  const [locating, setLocating] = useState(isLocating());
  useEffect(() => {
    listeners.add(setLocating);
    setLocating(isLocating());
    return () => { listeners.delete(setLocating); };
  }, []);
  return locating;
};

// 1st attempt: fast network/Wi-Fi fix (15s, accepts a cached fix up to 60s old).
// 2nd attempt: GPS, 30s, maximumAge 0 (iOS can be slow). Skipped if PERMISSION_DENIED.
// onError only fires after the FINAL attempt, so error.code === PERMISSION_DENIED
// really means the user denied. Returns a cancel function.
export const getPositionWithFallback = (onSuccess, onError) => {
  let cancelled = false;
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    inFlight = Math.max(0, inFlight - 1);
    notify();
  };
  const handleSuccess = (pos) => { finish(); if (!cancelled) onSuccess?.(pos); };
  const handleFinalError = (error) => { finish(); if (!cancelled) onError?.(error); };

  if (typeof navigator === "undefined" || !navigator.geolocation) {
    onError?.({ code: 2, message: "Geolocation not supported",
                PERMISSION_DENIED: 1, POSITION_UNAVAILABLE: 2, TIMEOUT: 3 });
    return () => {};
  }

  inFlight += 1;
  notify();

  navigator.geolocation.getCurrentPosition(
    handleSuccess,
    (error) => {
      if (cancelled) { finish(); return; }
      if (error.code === error.PERMISSION_DENIED) { handleFinalError(error); return; }
      navigator.geolocation.getCurrentPosition(handleSuccess, handleFinalError, {
        enableHighAccuracy: true, timeout: 30000, maximumAge: 0,
      });
    },
    { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
  );

  return () => { cancelled = true; finish(); };
};
export default getPositionWithFallback;

// ---- Live permission watching ----
const permissionListeners = new Set();
let permissionState = null;
let watching = false;

const setPermissionState = (next) => {
  if (!next || next === permissionState) return;
  const prev = permissionState;
  permissionState = next;
  if (prev === null) return; // first read = baseline only
  permissionListeners.forEach((fn) => fn(next, prev));
};
const recheckPermission = () => {
  if (!navigator.permissions?.query) return;
  navigator.permissions.query({ name: "geolocation" })
    .then((s) => setPermissionState(s.state)).catch(() => {});
};
const startWatchingPermission = () => {
  if (watching || typeof navigator === "undefined" || !navigator.permissions?.query) return;
  watching = true;
  navigator.permissions.query({ name: "geolocation" })
    .then((status) => {
      setPermissionState(status.state);
      status.onchange = () => setPermissionState(status.state);
    })
    .catch(() => { watching = false; });
  // Safari / older browsers don't fire `change` reliably: also re-check on focus.
  window.addEventListener("focus", recheckPermission);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) recheckPermission();
  });
};

// Calls onGranted() / onDenied() when the permission BECOMES granted / denied while
// `enabled` is true (pass a drawer/modal's `open` flag so closed ones stay quiet).
// Callbacks always see the latest render's state (stored in a ref).
export const useLocationPermissionSync = ({ enabled = true, onGranted, onDenied } = {}) => {
  const handlers = useRef({});
  handlers.current = { enabled, onGranted, onDenied };
  useEffect(() => {
    startWatchingPermission();
    const listener = (state) => {
      const { enabled: on, onGranted: g, onDenied: d } = handlers.current;
      if (!on) return;
      if (state === "granted") g?.();
      else if (state === "denied") d?.();
    };
    permissionListeners.add(listener);
    return () => { permissionListeners.delete(listener); };
  }, []);
};
